import { Component, OnInit, Inject, Optional } from '@angular/core';
import { MdDialogRef, MD_DIALOG_DATA, MdDialog, MdDialogConfig } from '@angular/material';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';

import { TransactionMonitoringModel } from '../entity-transaction-monitoring.model';
import { EntityIdDetails } from 'app/shared/models/entity-id-details.model';
import { EntityDetailsService } from 'app/entities/details/entity-details.service';
import { EntityRelationsService } from 'app/entities/relations/entity-relations.service';
import { EntityRelation } from 'app/shared/models/entity-relation.model';
import { EntityType } from 'app/shared/models/entity-id-details.model';
import { GenericSearchService } from 'app/shared/components/genericsearch/generic-search.service';
import { GenericSearchDetails } from 'app/shared/models/generic-search-details.model';
import { ConstantsService } from 'app/services/constants-service';
import { MessageService } from 'app/services/messages.service';
import { EntityTransactionMonitoringService } from '../entity-transaction-monitoring.service';
import { FinanceService } from 'app/finance/finance.service';
import { BankAccount } from 'app/shared/models/bank-account.model';
import { BankAccountComponent } from 'app/entities/finance/bankaccount/bank-account.component';
import { EntityTransactionProfileService } from 'app/entities/transaction-profile/entity-transaction-profile.service';
import { TransactionRule } from 'app/entities/transaction-profile/right.model';

@Component({
  selector: 'update-profile-wizard',
  templateUrl: './update-profile-wizard.component.html',
  styleUrls: ['./update-profile-wizard.component.css']
})
export class UpdateProfileWizardComponent implements OnInit {
  transactionRecord: TransactionMonitoringModel;
  entityIdDetails: EntityIdDetails;
  currentStep = 0;
  totalSteps = 8;

  // Step 0: Profile Selection
  transactionProfiles: TransactionRule[] = [];
  filteredProfiles: TransactionRule[] = [];
  displayedProfiles: TransactionRule[] = [];
  selectedProfile: TransactionRule = null;
  showAllProfiles = false;
  isCreatingNew = false;
  profileSearchQuery: string = '';

  // Step 1: Counterparty and Relation Selection
  relations: EntityRelation[] = [];
  selectedRelation: EntityRelation;
  searchQuery: string = '';
  searchResults: GenericSearchDetails[] = [];
  isSearching = false;
  showNoCounterpartyMessage = false;
  ConstantsService = ConstantsService;
  MessageService = MessageService;

  // Step 2: Name Match Verification
  nameMatchResult: { match: boolean; message: string } = null;
  bankAccountRequired = false;

  // Step 3: Bank Account Selection/Creation
  bankAccounts: BankAccount[] = [];
  selectedBankAccount: BankAccount;
  showCreateBankAccount = false;

  // Step 4: Profile Name
  profileNameForm: FormGroup;

  // Step 5: Rule Testing
  testRuleForm: FormGroup;
  ruleTestResult: { matches: boolean; message: string } = null;
  isTestingRule = false;

  // Step 6: Rule Editing (if needed)
  ruleEditForm: FormGroup;
  showRuleEditor = false;

  // Step 7: Recurring Transaction
  recurringForm: FormGroup;
  isRecurring = false;

  // Wizard state
  wizardData: any = {
    relationEntityId: null,
    bankAccountId: null,
    profileName: '',
    amountFrom: null,
    amountTo: null,
    frequency: null,
    frequencyPeriod: null,
    maxAmount: null,
    maxAmountPeriod: null
  };

  constructor(
    public dialogRef: MdDialogRef<UpdateProfileWizardComponent>,
    @Optional() @Inject(MD_DIALOG_DATA) public data: { transactionRecord: TransactionMonitoringModel },
    private entityDetailsService: EntityDetailsService,
    private entityRelationsService: EntityRelationsService,
    private genericSearchService: GenericSearchService,
    private transactionMonitoringService: EntityTransactionMonitoringService,
    private financeService: FinanceService,
    private formBuilder: FormBuilder,
    private dialog: MdDialog,
    private transactionProfileService: EntityTransactionProfileService
  ) {
    if (data && data.transactionRecord) {
      this.transactionRecord = data.transactionRecord;
    }
    this.entityIdDetails = this.entityDetailsService.getEntityIdDetails();
  }

  ngOnInit() {
    this.initializeForms();
    this.loadStep0Data();
  }

  initializeForms() {
    // Profile name form
    this.profileNameForm = this.formBuilder.group({
      profileName: ['', Validators.required]
    });

    // Rule test form (read-only, shows calculated values)
    this.testRuleForm = this.formBuilder.group({
      amountFrom: [{ value: '', disabled: true }],
      amountTo: [{ value: '', disabled: true }]
    });

    // Rule edit form
    this.ruleEditForm = this.formBuilder.group({
      amountFrom: [null, Validators.required],
      amountTo: [null, Validators.required]
    });

    // Recurring form
    this.recurringForm = this.formBuilder.group({
      isRecurring: [false],
      frequency: [null],
      frequencyPeriod: [null],
      maxAmount: [null],
      maxAmountPeriod: [null],
      updatedProfileName: ['']
    });
  }

  loadStep0Data() {
    // Load transaction profiles for the client
    if (this.entityIdDetails && this.entityIdDetails.clientId) {
      this.transactionProfileService.getTransactionRulesByClientId(this.entityIdDetails.clientId)
        .subscribe(profiles => {
          // Deduplicate rules: remove duplicates by keeping only the rule with the highest ID
          // This handles cases where the backend might return multiple versions of the same rule
          const profilesMap = new Map<number, TransactionRule>();
          (profiles || []).forEach(profile => {
            if (profile.id) {
              const existing = profilesMap.get(profile.id);
              if (!existing) {
                profilesMap.set(profile.id, profile);
              }
            }
          });
          
          // If there are still duplicates by name (different IDs but same name), keep the highest ID
          const nameMap = new Map<string, TransactionRule>();
          Array.from(profilesMap.values()).forEach(profile => {
            const key = (profile.name || '').trim() || `rule_${profile.id}`;
            const existing = nameMap.get(key);
            if (!existing || (profile.id && existing.id && profile.id > existing.id)) {
              nameMap.set(key, profile);
            }
          });
          
          this.transactionProfiles = Array.from(nameMap.values());
          this.filterProfiles();
        }, error => {
          console.error('Error loading transaction profiles:', error);
          this.transactionProfiles = [];
          this.filteredProfiles = [];
        });
    }
  }

  /**
   * Normalizes a name for comparison, matching the Java NameMatcher.normalize() logic.
   * Removes legal entity suffixes and normalizes abbreviations.
   */
  private normalizeName(name: string): string {
    if (!name || name.trim() === '') {
      return '';
    }

    let normalized = name.toLowerCase();

    // Normalize common abbreviations with periods to their non-period versions
    // Romanian/Italian: S.R.L. / SRL
    normalized = normalized.replace(/\b(s\.r\.l\.|s\.r\.l)\b/g, ' srl ');

    // US/International: L.L.C. / LLC, I.N.C. / INC, C.O. / CO, L.T.D. / LTD
    normalized = normalized.replace(/\b(l\.l\.c\.|l\.l\.c)\b/g, ' llc ');
    normalized = normalized.replace(/\b(i\.n\.c\.|i\.n\.c)\b/g, ' inc ');
    normalized = normalized.replace(/\b(c\.o\.|c\.o)\b/g, ' co ');
    normalized = normalized.replace(/\b(l\.t\.d\.|l\.t\.d)\b/g, ' ltd ');

    // Dutch: B.V. / BV, C.V. / CV, N.V. / NV
    normalized = normalized.replace(/\b(b\.v\.|b\.v)\b/g, ' bv ');
    normalized = normalized.replace(/\b(c\.v\.|c\.v)\b/g, ' cv ');
    normalized = normalized.replace(/\b(n\.v\.|n\.v)\b/g, ' nv ');

    // German/Austrian: G.m.b.H. / GmbH, A.G. / AG, K.G. / KG, O.H.G. / OHG, U.G. / UG
    normalized = normalized.replace(/\b(g\.m\.b\.h\.|g\.m\.b\.h)\b/g, ' gmbh ');
    normalized = normalized.replace(/\b(a\.g\.|a\.g)\b/g, ' ag ');
    normalized = normalized.replace(/\b(k\.g\.|k\.g)\b/g, ' kg ');
    normalized = normalized.replace(/\b(o\.h\.g\.|o\.h\.g)\b/g, ' ohg ');
    normalized = normalized.replace(/\b(u\.g\.|u\.g)\b/g, ' ug ');

    // French: S.A. / SA, S.A.R.L. / SARL, S.A.S. / SAS, S.N.C. / SNC
    normalized = normalized.replace(/\b(s\.a\.|s\.a)\b/g, ' sa ');
    normalized = normalized.replace(/\b(s\.a\.r\.l\.|s\.a\.r\.l)\b/g, ' sarl ');
    normalized = normalized.replace(/\b(s\.a\.s\.|s\.a\.s)\b/g, ' sas ');
    normalized = normalized.replace(/\b(s\.n\.c\.|s\.n\.c)\b/g, ' snc ');

    // Italian: S.P.A. / SPA
    normalized = normalized.replace(/\b(s\.p\.a\.|s\.p\.a)\b/g, ' spa ');

    // Spanish: S.L. / SL
    normalized = normalized.replace(/\b(s\.l\.|s\.l)\b/g, ' sl ');

    // UK/Commonwealth: P.L.C. / PLC, L.L.P. / LLP, L.P. / LP
    normalized = normalized.replace(/\b(p\.l\.c\.|p\.l\.c)\b/g, ' plc ');
    normalized = normalized.replace(/\b(l\.l\.p\.|l\.l\.p)\b/g, ' llp ');
    normalized = normalized.replace(/\b(l\.p\.|l\.p)\b/g, ' lp ');

    // Nordic: A.S. / AS, O.Y. / OY, A.B. / AB
    normalized = normalized.replace(/\b(a\.s\.|a\.s)\b/g, ' as ');
    normalized = normalized.replace(/\b(o\.y\.|o\.y)\b/g, ' oy ');
    normalized = normalized.replace(/\b(a\.b\.|a\.b)\b/g, ' ab ');

    // Asian: P.T. / PT, P.T.E. / PTE, P.V.T. / PVT
    normalized = normalized.replace(/\b(p\.t\.|p\.t)\b/g, ' pt ');
    normalized = normalized.replace(/\b(p\.t\.e\.|p\.t\.e)\b/g, ' pte ');
    normalized = normalized.replace(/\b(p\.v\.t\.|p\.v\.t)\b/g, ' pvt ');

    // South African: (Pty) / Pty
    normalized = normalized.replace(/\b\(?pty\)?\b/g, ' pty ');

    // Remove all legal entity suffixes
    normalized = normalized.replace(/\b(corp|corporation|limited|company|ltd|pty|sl|srl|llc|inc|co|bv|cv|nv|gmbh|ag|kg|ohg|ug|sa|sarl|sas|snc|spa|plc|llp|lp|as|oy|ab|pt|pte|pvt)\b/g, '');
    
    // Remove remaining special characters (periods, parentheses, etc.)
    normalized = normalized.replace(/[^a-z0-9\s]/g, '');
    
    // Normalize whitespace
    normalized = normalized.replace(/\s+/g, ' ').trim();

    return normalized;
  }

  filterProfiles() {
    if (!this.transactionRecord || !this.transactionRecord.counterPartyName) {
      this.filteredProfiles = this.transactionProfiles;
      this.applySearchFilter();
      return;
    }

    if (this.showAllProfiles) {
      this.filteredProfiles = this.transactionProfiles;
      this.applySearchFilter();
      return;
    }

    // Normalize transaction counterparty name
    const normalizedTransactionCp = this.normalizeName(this.transactionRecord.counterPartyName);
    const transactionCpWords = normalizedTransactionCp.split(/\s+/).filter(word => word.length > 0);
    
    this.filteredProfiles = this.transactionProfiles.filter(profile => {
      if (!profile.counterPartyName) {
        return false;
      }
      
      // Normalize profile counterparty name
      const normalizedProfileCp = this.normalizeName(profile.counterPartyName);
      const profileCpWords = normalizedProfileCp.split(/\s+/).filter(word => word.length > 0);
      
      // Check if at least one word from transaction counterparty matches a word in profile counterparty
      return transactionCpWords.some(txnWord => 
        txnWord.length > 2 && // Only match words longer than 2 characters
        profileCpWords.some(profileWord => 
          profileWord.includes(txnWord) || txnWord.includes(profileWord)
        )
      );
    });
    
    this.applySearchFilter();
  }

  applySearchFilter() {
    if (!this.profileSearchQuery || this.profileSearchQuery.trim() === '') {
      this.displayedProfiles = this.filteredProfiles;
      return;
    }

    const searchLower = this.profileSearchQuery.toLowerCase().trim();
    this.displayedProfiles = this.filteredProfiles.filter(profile => {
      const profileName = (profile.name || '').toLowerCase();
      const counterpartyName = (profile.counterPartyName || '').toLowerCase();
      return profileName.includes(searchLower) || counterpartyName.includes(searchLower);
    });
  }

  onSearchChange() {
    this.applySearchFilter();
  }

  toggleShowAllProfiles() {
    // showAllProfiles is already updated by ngModel, just filter the profiles
    this.filterProfiles();
  }

  selectExistingProfile(profile: TransactionRule) {
    this.selectedProfile = profile;
    this.isCreatingNew = false;
    this.loadProfileForEditing(profile);
    this.proceedToStep1();
  }

  createNewProfile() {
    this.selectedProfile = null;
    this.isCreatingNew = true;
    this.proceedToStep1();
  }

  loadProfileForEditing(profile: TransactionRule) {
    // Pre-fill wizard data with existing profile
    this.wizardData.profileName = profile.name || '';
    
    if (profile.counterPartyId) {
      this.wizardData.relationEntityId = profile.counterPartyId;
      // Try to find the relation in the relations list
      // This will be loaded in step 1
    }
    
    if (profile.counterPartyAccountNumber) {
      // We'll need to find the bank account by account number
      // This will be handled in step 3
    }
    
    this.wizardData.amountFrom = profile.amountFrom;
    this.wizardData.amountTo = profile.amountTo;
    this.wizardData.frequency = profile.frequency;
    this.wizardData.frequencyPeriod = profile.frequencyPeriod;
    this.wizardData.maxAmount = profile.maxAmount;
    this.wizardData.maxAmountPeriod = profile.maxAmountPeriod;
  }

  loadStep1Data() {
    // Check if transaction has counterparty
    if (!this.transactionRecord.counterPartyName || this.transactionRecord.counterPartyName.trim() === '') {
      this.showNoCounterpartyMessage = true;
      return;
    }

    // Load client relations
    if (this.entityIdDetails && this.entityIdDetails.entityId) {
      // Use getRelationshipsWithOffsetAndLimits to get all relations
      this.entityRelationsService.getRelationshipsWithOffsetAndLimits(
        this.entityIdDetails.entityId, 
        0, 
        100  // Get up to 100 relations
      )
        .subscribe(response => {
          if (response && response.arrResult && response.arrResult.relations) {
            this.relations = response.arrResult.relations;
          } else if (Array.isArray(response)) {
            this.relations = response;
          } else if (response && response.arrResult && Array.isArray(response.arrResult)) {
            this.relations = response.arrResult;
          } else {
            this.relations = [];
          }

          // If editing and we have a counterparty ID, try to select it
          if (this.selectedProfile && this.selectedProfile.counterPartyId) {
            const existingRelation = this.relations.find(r => r.entityId === this.selectedProfile.counterPartyId);
            if (existingRelation) {
              this.selectedRelation = existingRelation;
              this.wizardData.relationEntityId = existingRelation.entityId;
            }
          }
        }, error => {
          console.error('Error loading relations:', error);
          this.relations = [];
        });
    }

    // Pre-fill search with counterparty name
    this.searchQuery = this.transactionRecord.counterPartyName;
  }

  selectRelationFromSearch(result: GenericSearchDetails) {
    // Find relation in the relations list or create a new one
    const existingRelation = this.relations.find(r => r.entityId === result.entityId);
    if (existingRelation) {
      this.selectedRelation = existingRelation;
    } else {
      // Create a new relation - this would need to be implemented
      // For now, we'll just set the entity ID
      this.wizardData.relationEntityId = result.entityId;
      
      // Convert entityType string to EntityType enum
      let entityType: EntityType = EntityType.ORGANISATION;
      if (result.entityType) {
        if (result.entityType === 'PERSON' || result.entityType === 'Person') {
          entityType = EntityType.PERSON;
        } else if (result.entityType === 'CLIENT' || result.entityType === 'Client') {
          entityType = EntityType.CLIENT;
        } else {
          entityType = EntityType.ORGANISATION;
        }
      }
      
      this.selectedRelation = {
        id: 0,
        entityId: result.entityId,
        name: result.clientName || '',
        entityType: entityType
      } as EntityRelation;
    }
    this.proceedToStep2();
  }

  selectExistingRelation(relation: EntityRelation) {
    this.selectedRelation = relation;
    this.wizardData.relationEntityId = relation.entityId;
    this.proceedToStep2();
  }

  proceedToStep1() {
    if (!this.selectedProfile && !this.isCreatingNew) {
      alert('Please select an existing profile or choose to create a new one');
      return;
    }

    this.currentStep = 1;
    this.loadStep1Data();
  }

  proceedToStep2() {
    if (!this.selectedRelation) {
      alert('Please select a relation');
      return;
    }

    // Verify name match - check both entity name and bank account names
    const transactionCpName = this.transactionRecord.counterPartyName;
    const relationName = this.selectedRelation.name;

    // First check if name matches the relation entity name
    this.transactionMonitoringService.checkNameMatch(transactionCpName, relationName)
      .subscribe(entityMatchResponse => {
        const entityMatches = entityMatchResponse.arrResult.match;
        
        if (entityMatches) {
          // Name matches entity, we're good
          this.setNameMatchResult(true, transactionCpName, relationName, null);
        } else {
          // If it doesn't match the entity name, check bank account names
          this.financeService.getBankAccountsForEntity(this.selectedRelation.entityId)
            .subscribe(accounts => {
              if (!accounts || accounts.length === 0) {
                // No bank accounts, no match
                this.setNameMatchResult(false, transactionCpName, relationName, null);
                return;
              }

              // Check each bank account name sequentially
              let checkedCount = 0;
              let bankAccountMatch = false;
              let matchedBankAccountName: string | null = null;

              const checkNextAccount = () => {
                if (checkedCount >= accounts.length) {
                  // All accounts checked, set final result
                  this.setNameMatchResult(bankAccountMatch, transactionCpName, relationName, matchedBankAccountName);
                  return;
                }

                const account = accounts[checkedCount];
                checkedCount++;

                if (account.accountName) {
                  // Check if transaction name matches this bank account name
                  this.transactionMonitoringService.checkNameMatch(transactionCpName, account.accountName)
                    .subscribe(accountMatchResponse => {
                      if (accountMatchResponse.arrResult.match && !bankAccountMatch) {
                        bankAccountMatch = true;
                        matchedBankAccountName = account.accountName;
                        // Found a match, set result immediately
                        this.setNameMatchResult(true, transactionCpName, relationName, matchedBankAccountName);
                      } else {
                        // Continue checking other accounts
                        checkNextAccount();
                      }
                    }, error => {
                      console.error('Error checking bank account name match:', error);
                      checkNextAccount();
                    });
                } else {
                  // No account name, skip to next
                  checkNextAccount();
                }
              };

              // Start checking accounts
              checkNextAccount();
            }, error => {
              console.error('Error loading bank accounts:', error);
              // If we can't load bank accounts, just use the entity name match result
              this.setNameMatchResult(false, transactionCpName, relationName, null);
            });
        }
      });
  }

  setNameMatchResult(match: boolean, transactionCpName: string, relationName: string, bankAccountName: string | null) {
    this.nameMatchResult = {
      match: match,
      message: match 
        ? `Names match: "${transactionCpName}" matches ${bankAccountName ? `bank account "${bankAccountName}"` : `"${relationName}"`}`
        : `No match found: "${transactionCpName}" does not match "${relationName}"${bankAccountName ? ` or "${bankAccountName}"` : ''}. A bank account with the exact name will be required.`
    };
    this.bankAccountRequired = !match;
    this.currentStep = 2;
  }

  proceedToStep3() {
    this.currentStep = 3;
    this.loadBankAccounts();
  }

  loadBankAccounts(): Promise<void> {
    return new Promise((resolve) => {
      if (this.selectedRelation && this.selectedRelation.entityId) {
        this.financeService.getBankAccountsForEntity(this.selectedRelation.entityId)
          .subscribe(accounts => {
            this.bankAccounts = accounts || [];
            
            // If editing and we have a bank account number, try to select it
            if (this.selectedProfile && this.selectedProfile.counterPartyAccountNumber) {
              const existingAccount = this.bankAccounts.find(acc => 
                acc.accountNumber === this.selectedProfile.counterPartyAccountNumber
              );
              if (existingAccount) {
                this.selectedBankAccount = existingAccount;
                this.wizardData.bankAccountId = existingAccount.accountId;
              }
            }
            resolve();
          }, error => {
            console.error('Error loading bank accounts:', error);
            resolve();
          });
      } else {
        resolve();
      }
    });
  }

  selectBankAccount(account: BankAccount) {
    this.selectedBankAccount = account;
    this.wizardData.bankAccountId = account.accountId;
  }

  openCreateBankAccountDialog() {
    const config = new MdDialogConfig();
    config.disableClose = true;
    config.width = '600px';

    const dialogRef = this.dialog.open(BankAccountComponent, config);
    dialogRef.componentInstance.action = 'Add';
    
    // Store the account name and number we're creating for matching later
    const accountNameToMatch = this.transactionRecord.counterPartyName;
    const counterpartyEntityId = this.selectedRelation.entityId;
    let accountNumberToMatch = null;
    
    // Set accountHolderEntityId after ngOnInit runs (which overwrites it)
    // We need to set it after the component initializes
    setTimeout(() => {
      // Override the accountHolderEntityId that was set in ngOnInit
      dialogRef.componentInstance.accountHolderEntityId = counterpartyEntityId;
      
      // Initialize form if not already done
      if (!dialogRef.componentInstance.bankAccountForm) {
        dialogRef.componentInstance.createForm();
      }
      
      // Set account name from transaction counterparty name
      if (dialogRef.componentInstance.bankAccountForm) {
        dialogRef.componentInstance.bankAccountForm.controls['accountName'].setValue(accountNameToMatch);
        // Store account number if user enters it
        dialogRef.componentInstance.bankAccountForm.controls['accountNumber'].valueChanges.subscribe(value => {
          accountNumberToMatch = value;
        });
      }
    }, 200);

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.flagResult) {
        // Reload bank accounts to get the newly created one
        this.loadBankAccounts().then(() => {
          // Try to find the account by name or number
          let newAccount = null;
          
          if (accountNumberToMatch) {
            newAccount = this.bankAccounts.find(acc => acc.accountNumber === accountNumberToMatch);
          }
          
          if (!newAccount) {
            newAccount = this.bankAccounts.find(acc => acc.accountName === accountNameToMatch);
          }
          
          if (!newAccount && this.bankAccounts.length > 0) {
            // If we can't find by name/number, select the last one (most likely the new one)
            newAccount = this.bankAccounts[this.bankAccounts.length - 1];
          }
          
          if (newAccount) {
            this.selectBankAccount(newAccount);
            // Automatically proceed to next step
            this.proceedToStep4();
          } else {
            // If still no account found, try reloading once more
            setTimeout(() => {
              this.loadBankAccounts().then(() => {
                if (this.bankAccounts.length > 0) {
                  const lastAccount = this.bankAccounts[this.bankAccounts.length - 1];
                  this.selectBankAccount(lastAccount);
                  this.proceedToStep4();
                }
              });
            }, 500);
          }
        });
      }
    });
  }

  proceedToStep4() {
    if (this.bankAccountRequired && !this.selectedBankAccount) {
      alert('Please select or create a bank account');
      return;
    }

    // Set default profile name if not editing
    if (!this.selectedProfile || !this.wizardData.profileName) {
      // Note: EntityIdDetails doesn't have clientName, so we'll use a default
      // In a real scenario, you might want to fetch the client name from the service
      const clientName = 'Client'; // Could be enhanced to fetch actual client name
      const relationName = this.selectedRelation.name || 'Relation';
      const defaultName = `${clientName} - ${relationName}`;
      this.wizardData.profileName = defaultName;
    }
    
    this.profileNameForm.controls['profileName'].setValue(this.wizardData.profileName);
    this.currentStep = 4;
  }

  proceedToStep5() {
    if (!this.profileNameForm.valid) {
      alert('Please provide a profile name');
      return;
    }

    this.wizardData.profileName = this.profileNameForm.controls['profileName'].value;

    // Calculate amount range: 5% less and 5% more than transaction amount
    // Only if not editing (editing uses existing values)
    if (!this.selectedProfile || !this.wizardData.amountFrom || !this.wizardData.amountTo) {
      const amount = Math.abs(this.transactionRecord.amount);
      const amountFrom = amount * 0.95;
      const amountTo = amount * 1.05;

      this.wizardData.amountFrom = amountFrom;
      this.wizardData.amountTo = amountTo;
    }

    this.testRuleForm.controls['amountFrom'].setValue(this.wizardData.amountFrom.toFixed(2));
    this.testRuleForm.controls['amountTo'].setValue(this.wizardData.amountTo.toFixed(2));

    this.currentStep = 5;
    this.testRuleMatch();
  }

  testRuleMatch() {
    this.isTestingRule = true;
    this.ruleTestResult = null;

    const testData = {
      transactionId: this.transactionRecord.id,
      amountFrom: this.wizardData.amountFrom,
      amountTo: this.wizardData.amountTo,
      counterPartyId: this.selectedRelation.entityId,
      bankAccountId: this.selectedBankAccount ? this.selectedBankAccount.accountId : null
    };

    this.transactionMonitoringService.testTransactionRuleMatch(testData)
      .subscribe(response => {
        this.isTestingRule = false;
        this.ruleTestResult = {
          matches: response.arrResult.matches,
          message: response.arrResult.matches
            ? 'Rule matches the transaction!'
            : response.arrResult.message || 'Rule does not match the transaction. Please adjust the amount range.'
        };

        if (!response.arrResult.matches) {
          this.showRuleEditor = true;
          this.ruleEditForm.controls['amountFrom'].setValue(this.wizardData.amountFrom);
          this.ruleEditForm.controls['amountTo'].setValue(this.wizardData.amountTo);
        }
      }, error => {
        this.isTestingRule = false;
        this.ruleTestResult = {
          matches: false,
          message: 'Error testing rule: ' + (error.message || 'Unknown error')
        };
      });
  }

  updateRuleAndRetest() {
    if (!this.ruleEditForm.valid) {
      alert('Please provide valid amount range');
      return;
    }

    this.wizardData.amountFrom = this.ruleEditForm.controls['amountFrom'].value;
    this.wizardData.amountTo = this.ruleEditForm.controls['amountTo'].value;
    this.testRuleForm.controls['amountFrom'].setValue(this.wizardData.amountFrom);
    this.testRuleForm.controls['amountTo'].setValue(this.wizardData.amountTo);

    this.testRuleMatch();
  }

  proceedToStep6() {
    if (!this.ruleTestResult || !this.ruleTestResult.matches) {
      alert('Please ensure the rule matches the transaction before proceeding');
      return;
    }

    // Step 6 is implicit - just proceed to step 7
    this.currentStep = 7;
  }

  proceedToStep7() {
    this.currentStep = 7;
  }

  saveProfile() {
    const isRecurring = this.recurringForm.controls['isRecurring'].value;
    
    if (isRecurring) {
      this.wizardData.frequency = this.recurringForm.controls['frequency'].value;
      this.wizardData.frequencyPeriod = this.recurringForm.controls['frequencyPeriod'].value;
      this.wizardData.maxAmount = this.recurringForm.controls['maxAmount'].value;
      this.wizardData.maxAmountPeriod = this.recurringForm.controls['maxAmountPeriod'].value;
      
      const updatedName = this.recurringForm.controls['updatedProfileName'].value;
      if (updatedName && updatedName.trim() !== '') {
        this.wizardData.profileName = updatedName;
      }
    }

    const createData = {
      transactionId: this.transactionRecord.id,
      relationEntityId: this.selectedRelation.entityId,
      bankAccountId: this.wizardData.bankAccountId,
      profileName: this.wizardData.profileName,
      amountFrom: this.wizardData.amountFrom,
      amountTo: this.wizardData.amountTo,
      frequency: this.wizardData.frequency,
      frequencyPeriod: this.wizardData.frequencyPeriod,
      maxAmount: this.wizardData.maxAmount,
      maxAmountPeriod: this.wizardData.maxAmountPeriod,
      isIncoming: this.transactionRecord.amount >= 0,
      profileId: this.selectedProfile ? this.selectedProfile.id : null  // Include profile ID if editing
    };

    this.transactionMonitoringService.createTransactionProfileFromRecord(createData)
      .subscribe(response => {
        if (response.flagResult) {
          const action = this.selectedProfile ? 'updated' : 'created';
          alert(`Transaction profile ${action} successfully!`);
          this.dialogRef.close(true);
        } else {
          const action = this.selectedProfile ? 'update' : 'create';
          alert(`Failed to ${action} transaction profile: ` + (response.message || 'Unknown error'));
        }
      }, error => {
        const action = this.selectedProfile ? 'updating' : 'creating';
        alert(`Error ${action} transaction profile: ` + (error.message || 'Unknown error'));
      });
  }

  nextStep() {
    if (this.currentStep < this.totalSteps) {
      // Validation and step-specific logic is handled in proceedToStepX methods
      // This is a fallback
      this.currentStep++;
    }
  }

  previousStep() {
    if (this.currentStep > 0) {
      this.currentStep--;
    }
  }

  cancel() {
    this.dialogRef.close(false);
  }
}




// WEBPACK FOOTER //
// ./src/app/entities/transaction-monitoring/update-profile-wizard/update-profile-wizard.component.ts